<template>
  <v-row>
    <v-col cols="12">
      <v-form ref="crudForm" @submit.prevent="submit">
        <v-row>
          <v-col cols="12">
            <v-text-field label="Place Name"
                          v-model="model.Title"
                          maxlength="128"
                          :rules="rules.Title" />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="6">
            <v-text-field label="Address 1"
                          maxlength="64"
                          v-model="model.Address1" />
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field label="Address 2"
                          maxlength="64"
                          v-model="model.Address2" />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="3">
            <v-text-field label="City"
                          maxlength="64"
                          v-model="model.City" />
          </v-col>
          <v-col cols="12" md="3">
            <v-text-field label="State"
                          v-model="model.State"
                          v-mask="model.Country === 'US' ? masks.state : masks.noMask"
                          :maxlength="model.Country === 'US' ? 2 : 64" />
          </v-col>
          <v-col cols="12" md="3">
            <v-text-field label="Postal Code"
                          v-model="model.Zip"
                          v-mask="model.Country === 'US' ? masks.zip : masks.noMask"
                          :maxlength="model.Country === 'US' ? 5 : 64" />
          </v-col>
          <v-col cols="12" md="3">
            <v-autocomplete label="Country"
                            append-icon="add"
                            item-text="Title"
                            item-value="Code"
                            prepend-icon="public"
                            v-model="model.Country"
                            :rules="rules.Country"
                            :items="countries.items"
                            :loading="countries.loading"
                            @keydown.enter.native.prevent="">
              <template v-slot:prepend-item>
                <v-list-item v-if="$store.getters.can('Admin')"
                             ripple
                             @click="$refs.CountryForm.open()">
                  <v-list-item-icon>
                    <v-icon>add</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    Create New...
                  </v-list-item-content>
                </v-list-item>
                <v-divider />
              </template>
            </v-autocomplete>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="6">
            <v-text-field class="no-spinner"
                          label="Latitude"
                          type="number"
                          v-model="model.Latitude" />
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field class="no-spinner"
                          label="Longitude"
                          type="number"
                          v-model="model.Longitude" />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <v-btn type="submit" color="primary" :loading="loading">
              {{ id ? 'Save' : 'Create' }}
            </v-btn>
          </v-col>
        </v-row>
      </v-form>
      <NestedForm form="CountryForm"
                  title="Create Country"
                  ref="CountryForm"
                  v-if="$store.getters.can('Admin')"
                  @update="addNewCountry" />
    </v-col>
  </v-row>
</template>

<script>
import { mask } from 'vue-the-mask';
import FormMixins from '@/components/Mixins/Forms';
import { rules } from '@/util/Helpers';

export default {
  name: 'PlaceForm',
  directives: {
    mask,
  },
  mixins: [FormMixins],
  props: {
    Id: [String, Number],
    Title: String,
    Address1: String,
    Address2: String,
    City: String,
    State: String,
    Zip: String,
    Country: {
      type: String,
      default: 'United States',
    },
    Latitude: Number,
    Longitude: Number,
  },
  data() {
    return {
      id: this.Id,
      model: {
        Title: this.Title,
        Address1: this.Address1,
        Address2: this.Address2,
        City: this.City,
        State: this.State,
        Zip: this.Zip,
        Country: this.Country,
        Latitude: this.Latitude,
        Longitude: this.Longitude,
      },
      rules: {
        Title: [
          (v) => rules.required(v, 'Please enter a Place Name'),
        ],
        State: [
          (v) => rules.pattern(v, /^[A-Z]{2}$/, 'Please enter a valid US state format'),
        ],
        Country: [
          (v) => rules.required(v, 'Please select a Country'),
        ],
      },
      loading: false,
      countries: {
        loading: true,
        items: [],
      },
      masks: {
        state: 'AA',
        zip: '#####',
        noMask: {
          mask: '*'.repeat(64),
          tokens: {
            '*': { pattern: /[a-zA-Z0-9\s]/ },
          },
        },
      },
    };
  },
  methods: {
    submit() {
      if (this.$refs.crudForm.validate()) {
        this.loading = true;
        if (this.id) {
          this.update();
        } else {
          this.create();
        }
        this.loading = false;
      } else {
        this.$refs.crudForm.inputs.every(this.goToFirstError);
      }
    },
    async create() {
      try {
        const response = await this.$store.dispatch('post', {
          api: 'Place',
          data: this.model,
        });
        this.$emit('snackbar', 'Place successfully created!');
        this.$emit('crud:create', response);
        this.$emit('crud:success', response);
        this.id = response.data.Id;
      } catch (e) {
        this.$emit('crud:error', e);
      }
    },
    async update() {
      try {
        const response = await this.$store.dispatch('put', {
          api: `Place/${this.id}`,
          data: this.model,
        });
        this.$emit('snackbar', 'Place successfully updated!');
        this.$emit('crud:update', response);
        this.$emit('crud:success', response);
      } catch (e) {
        this.$emit('crud:error', e);
      }
    },
    searchCountries(title) {
      const results = this.countries.items.filter((e) => e.Title === title);
      return results.length ? results[0].Code : null;
    },
    addNewCountry(response) {
      this.countries.items.push(response);
      this.model.Country = response.Code;
    },
  },
  created() {
    this.$store.dispatch('get', 'Country').then((response) => {
      this.countries.items = response.data;
      this.model.Country = this.searchCountries(this.Country);
    }).finally(() => {
      this.countries.loading = false;
    });
  },
};
</script>
